import { Component, Input, OnInit, Inject, AfterViewInit } from '@angular/core';
import { OnyxEvent } from '../../models/event';
import { Observable } from 'rxjs/Observable';
import { List } from 'immutable';
import { ReservationCompare } from '../../models/reservation-compare';
import { ReservationStatusCode } from '../../models/reservation-status-code.enum';
import { ReservationCompareStore } from '../../service/reservation-compare-store';
import { LogService } from '../../../shared/log/log.service';
import { ReservationUtils } from '../../utils/reservation-utils';
import { MatchTypeEnum } from '../../models/match-type.enum';
import { PageScrollService, PageScrollInstance } from 'ng2-page-scroll';
import { DOCUMENT } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { NumberUtils } from '../../../shared/utils/number.util';
import { Rsvp } from '../../models/rsvp';
import { Reservation } from '../../models/reservation';

@Component({
  selector: 'pioneer-reservation-detail-table',
  templateUrl: './reservation-detail-table.component.html'
})
export class ReservationDetailTableComponent implements OnInit, AfterViewInit {

  private onyxEvent: OnyxEvent;

  public reservationCompareList: Observable<List<ReservationCompare>>;

  public statusCodes: Observable<ReservationStatusCode[]>;

  public MatchTypeEnum = MatchTypeEnum;

  public pageScrollId: string = null;

  public ui = {
    showCards: false,
    showNoMatchesCard: false,
    showNoMatchesDetails: false,
    showNoMatchesWarningCard: false,
    showPartialMatchesCard: false,
    showPartialMatchesDetails: false,
    showPartialMatchesWarningCard: false,
    showExactMatchesCard: false,
    showExactMatchesDetails: false,
    showExactMatchesWarningCard: false,
    showNoReservationsWarning: false,
    showNoMatchRsvpTotal: false
  };

  constructor(private reservationCompareStore: ReservationCompareStore,
              private translateService: TranslateService,
              private logService: LogService,
              private pageScrollService: PageScrollService,
              @Inject(DOCUMENT) private document: any) {
  }

  @Input('event')
  set event(event: OnyxEvent) {
    this.onyxEvent = event;
  }

  ngOnInit() {
    this.statusCodes = this.reservationCompareStore.getActiveStatusCodes();
    this.reservationCompareList = this.reservationCompareStore.getReservationList();

    // all cards
    this.isReservationCompareListEmpty().subscribe((value: boolean) => {
      this.ui.showNoReservationsWarning = value;
      this.ui.showCards = !value;
    });

    // no matches
    this.isUnfilteredMatchListEmpty(MatchTypeEnum.NO_MATCH).subscribe((value: boolean) => {
      this.ui.showNoMatchesCard = !value;
    });
    this.isFilteredMatchListEmpty(MatchTypeEnum.NO_MATCH).subscribe((value: boolean) => {
      this.ui.showNoMatchesDetails = !value;
      this.ui.showNoMatchesWarningCard = value;
    });

    // show RSVP total in 'no matches' section if RSVPs are present
    this.reservationCompareStore.areRsvpsPresent().subscribe((rsvpsPresent: boolean) => {
      this.ui.showNoMatchRsvpTotal = rsvpsPresent;
    });

    // partial matches
    this.isUnfilteredMatchListEmpty(MatchTypeEnum.PARTIAL_MATCH).subscribe((value: boolean) => {
      this.ui.showPartialMatchesCard = !value;
    });
    this.isFilteredMatchListEmpty(MatchTypeEnum.PARTIAL_MATCH).subscribe((value: boolean) => {
      this.ui.showPartialMatchesDetails = !value;
      this.ui.showPartialMatchesWarningCard = value;
    });

    // exact matches
    this.isUnfilteredMatchListEmpty(MatchTypeEnum.EXACT_MATCH).subscribe((value: boolean) => {
      this.ui.showExactMatchesCard = !value;
    });
    this.isFilteredMatchListEmpty(MatchTypeEnum.EXACT_MATCH).subscribe((value: boolean) => {
      this.ui.showExactMatchesDetails = !value;
      this.ui.showExactMatchesWarningCard = value;
    });

  }

  ngAfterViewInit() {
    const rsvpId = this.reservationCompareStore.selectedRsvpId;
    const reservationId = this.reservationCompareStore.selectedReservationId;
    this.logService.verbose(`rsvp: ${rsvpId}, reservation: ${reservationId}`);

    let elem: Element;
    if (reservationId) {
      const id = this.getReservationId(reservationId);
      elem = document.querySelector(`[data-res-id=${id}]`);
      elem.classList.add('highlight');
      this.logService.verbose(`page scrolling to selector: [data-res-id=${id}]`);
      this.scrollToElement(<HTMLElement>elem);
    } else if (rsvpId) {
      const id = this.getRsvpId(rsvpId);
      elem = document.querySelector(`[data-rsvp-id=${id}]`);
      elem.classList.add('highlight');
      this.logService.verbose(`page scrolling to selector: [data-rsvp-id=${id}]`);
      this.scrollToElement(<HTMLElement>elem);
    }
  }

  private scrollToElement(element: HTMLElement) {
    const pageScrollInstance: PageScrollInstance = PageScrollInstance.simpleInstance(this.document, element);
    this.pageScrollService.start(pageScrollInstance);
  }

  getFilteredComparisonList(): Observable<List<ReservationCompare>> {
    return this.reservationCompareStore.getCurrentFilteredReservations();
  }

  getMatches(matchType: MatchTypeEnum): Observable<List<ReservationCompare>> {
    return this.getFilteredComparisonList().map((comparisonList: List<ReservationCompare>) => {
      return comparisonList.filter((comparison: ReservationCompare) => comparison.matchType === matchType.toString()).toList();
    });
  }

  getUnfilteredMatches(matchType: MatchTypeEnum): Observable<List<ReservationCompare>> {
    return this.reservationCompareList.map((comparisonList: List<ReservationCompare>) => {
      return comparisonList.filter((comparison: ReservationCompare) => comparison.matchType === matchType.toString()).toList();
    });
  }

  getReservationFilteredRevenue(matchType: MatchTypeEnum): Observable<number> {
    return ReservationUtils.calculateRevenueTotal(this.getMatches(matchType));
  }

  getReservationFilteredCommission(matchType: MatchTypeEnum): Observable<number> {
    return ReservationUtils.calculateCommissionTotal(this.getMatches(matchType));
  }

  getReservationTotalCommission(matchType: MatchTypeEnum): Observable<number> {
    return ReservationUtils.calculateCommissionTotal(this.getUnfilteredMatches(matchType));
  }

  getRsvpFilteredCommission(matchType: MatchTypeEnum): Observable<number> {
    return ReservationUtils.calculateRsvpCommissionTotal(this.getMatches(matchType));
  }

  getRsvpId(value: Rsvp | number) {
    let rsvpId: number;
    if (typeof value === 'number') {
      rsvpId = <number>value;
    } else if (value) {
      rsvpId = value.eventRsvpId;
    } else {
      return '';
    }

    if (rsvpId) {
      return `rsvp-${rsvpId}`;
    } else {
      return '';
    }
  }

  getReservationId(value: Reservation | number) {
    let reservationId: number;
    if (typeof value === 'number') {
      reservationId = <number>value;
    } else if (value) {
      reservationId = value.eventReservationId;
    } else {
      return '';
    }

    if (reservationId) {
      return `res-${reservationId}`;
    } else {
      return '';
    }
  }

  getRsvpTotalCommission(matchType: MatchTypeEnum): Observable<number> {
    return ReservationUtils.calculateRsvpCommissionTotal(this.getUnfilteredMatches(matchType));
  }

  isReservationCompareListEmpty(): Observable<boolean> {
    return ReservationUtils.isListEmpty(this.reservationCompareList);
  }

  isRsvpMatchListEmpty(matchType: MatchTypeEnum): Observable<boolean> {
    return ReservationUtils.isListEmpty(this.getMatches(matchType).map(list => ReservationUtils.onlyRsvpFilter(list)));
  }

  isFilteredMatchListEmpty(matchType: MatchTypeEnum): Observable<boolean> {
    return this.reservationCompareStore.isFilteredMatchListEmpty(matchType);
  }

  isUnfilteredMatchListEmpty(matchType: MatchTypeEnum): Observable<boolean> {
    return ReservationUtils.isListEmpty(this.getUnfilteredMatches(matchType));
  }

  hasFilteredOutResults(): Observable<boolean> {
    return ReservationUtils.isListEmpty(this.getFilteredComparisonList()).flatMap(filteredListIsEmpty => {
      return this.isReservationCompareListEmpty().map(isReservationListEmpty => filteredListIsEmpty !== isReservationListEmpty);
    });
  }

  getCommissionDelta(matchType: MatchTypeEnum): Observable<number> {
    const reservationCommissionObj = this.getReservationTotalCommission(matchType);
    const rsvpCommissionObj = this.getRsvpTotalCommission(matchType);

    return reservationCommissionObj.flatMap(reservationCommission => {
      return rsvpCommissionObj.map(rsvpCommission => {
        return reservationCommission - rsvpCommission;
      });
    }).map(total => NumberUtils.round2Fixed(total));
  }

  getCommissionDeltaText(matchType: MatchTypeEnum): Observable<string> {
    return this.getCommissionDelta(matchType).flatMap(delta => {
      if (delta > 0) {
        return this.translateService.get('EVENT.RESERVATION.TOOLTIP_COMMISSION_DIFFERENCE_MORE');
      } else if (delta < 0) {
        return this.translateService.get('EVENT.RESERVATION.TOOLTIP_COMMISSION_DIFFERENCE_LESS');
      } else {
        return this.translateService.get('EVENT.RESERVATION.TOOLTIP_COMMISSION_DIFFERENCE_EQUAL');
      }
    });
  }

  getTooltipHeaderText(matchType: MatchTypeEnum): Observable<string> {
    switch (matchType) {
      case MatchTypeEnum.EXACT_MATCH:
        return this.translateService.get('EVENT.RESERVATION.TOOLTIP_EXACT_MATCH_COMMISSION_TITLE');
      case MatchTypeEnum.PARTIAL_MATCH:
        return this.translateService.get('EVENT.RESERVATION.TOOLTIP_PARTIAL_MATCH_COMMISSION_TITLE');
      case MatchTypeEnum.NO_MATCH:
        return this.translateService.get('EVENT.RESERVATION.TOOLTIP_NO_MATCH_COMMISSION_TITLE');
      default:
        return Observable.of('');
    }
  }

  resetStatusCodes(): void {
    this.reservationCompareStore.resetStatusCodes();
  }

  totalCommissionClass(matchType: MatchTypeEnum): Observable<string> {
    let rsvpCount: number;
    return this.reservationCompareStore.areRsvpsPresent().flatMap(result => {
      if (!result && matchType === MatchTypeEnum.NO_MATCH) {
        return Observable.of('') ;
      } else {
        return this.getCommissionDelta(matchType).map(total => {
          return total > 0 ? 'commission-up' : total < 0 ? 'commission-down' : '';
        });
      }
    });
  }

  getStatusClass(compare: ReservationCompare): string {
    if (compare.reservation) {
      switch (compare.reservation.status) {
        case 'R':
          return 'res-status-reserved';
        case 'I':
          return 'res-status-checked-in';
        case 'O':
          return 'res-status-checked-out';
        case 'C':
          return 'res-status-cancelled';
        case 'N':
          return 'res-status-no-show';
        default:
          return '';
      }
    }
    return '';
  }

  isTotalCommissionUp(matchType: MatchTypeEnum): Observable<boolean> {
    return this.getCommissionDelta(matchType).map(total => total > 0);
  }

  isTotalCommissionDown(matchType: MatchTypeEnum): Observable<boolean> {
    return this.getCommissionDelta(matchType).map(total => total < 0);
  }

  isCommissionUp(compare: ReservationCompare): boolean {
    return compare.rsvp && compare.delta && compare.delta.commission > 0;
  }

  isCommissionDown(compare: ReservationCompare): boolean {
    return compare.rsvp && compare.delta && compare.delta.commission < 0;
  }

  isRevenueUp(compare: ReservationCompare): boolean {
    return compare.rsvp && compare.delta && compare.delta.revenue > 0;
  }

  isRevenueDown(compare: ReservationCompare): boolean {
    return compare.rsvp && compare.delta && compare.delta.revenue < 0;
  }

  isRoomRateUp(compare: ReservationCompare): boolean {
    return compare.rsvp && compare.delta && compare.delta.roomRate > 0;
  }

  isRoomRateDown(compare: ReservationCompare): boolean {
    return compare.rsvp && compare.delta && compare.delta.roomRate < 0;
  }

  isNightsUp(compare: ReservationCompare): boolean {
    return compare.rsvp && compare.delta && compare.delta.nights > 0;
  }

  isNightsDown(compare: ReservationCompare): boolean {
    return compare.rsvp && compare.delta && compare.delta.nights < 0;
  }

  getNoMatchCardHeaderTitle(): Observable<string> {
    return this.reservationCompareStore.areRsvpsPresent().map(result => {
      return result ? 'EVENT.RESERVATION.NO_MATCH_TITLE' : 'EVENT.RESERVATION.NO_RSVP_TITLE';
    });
  }

  getNoMatchCardHeaderTitleTooltip(): Observable<string> {
    return ReservationUtils.getNumberOfRsvps(this.reservationCompareStore.getReservationList()).map(size => {
      return size > 0 ? 'EVENT.RESERVATION.NO_MATCH_TITLE_TOOLTIP' : 'EVENT.RESERVATION.NO_RSVP_TITLE_TOOLTIP';
    });
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/UAT-GroupPay/workspace/pioneer-web/src/app/event/reservations/reservation-detail-table/reservation-detail-table.component.ts